import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatBytes, formatDuration, type AttachmentDto } from '@initiative/shared';
import { FotoWerkstatt } from '../media/FotoWerkstatt.js';
import { VideoWerkstatt } from '../video/VideoWerkstatt.js';
import { dialogeOffen } from '../../lib/dialogVerlauf.js';
import { mediaSrc, standbildHolen } from '../media/helpers.js';
import { FernsehKnopf } from '../fernseher/FernsehKnopf.js';
import { CastKnopf } from '../fernseher/CastKnopf.js';
import { DateiAktionen } from '../media/DateiAktionen.js';

interface FileViewerProps {
  items: AttachmentDto[];
  index: number;
  onClose: () => void;
  /**
   * Wohin eine bearbeitete Fassung gehört – bei einer Sammlung: in dieselbe.
   * Ohne das erscheinen Bearbeiten und Sticker gar nicht erst.
   */
  ablegen?: (blob: Blob, name: string) => Promise<void>;
  zielName?: string;
  /**
   * Was mit der gerade gezeigten Datei geschehen darf.
   *
   * Ohne diese Angabe erscheint der Knopf gar nicht – im Betrachter einer
   * Sammlung, die man nur ansehen darf, wäre er eine Einladung zu einer
   * Absage des Servers.
   */
  aktionen?: {
    /** Diese eine Datei hier wegräumen. Fehlt der Rückruf, gibt es kein Löschen. */
    loeschen?: (datei: AttachmentDto) => Promise<void>;
    loeschText?: (datei: AttachmentDto) => string;
    onGeaendert?: () => void;
  };
}

/**
 * Der eingebaute Betrachter für alles, was in einer Sammlung liegen kann.
 *
 * Bild, Video, Ton und PDF werden hier gezeigt, ohne die App zu verlassen.
 * Alles Übrige bekommt eine ehrliche Karte mit Namen, Größe und einem Knopf
 * zum Öffnen – ein leerer schwarzer Kasten wäre schlechter als die Auskunft,
 * dass dieser Dateityp sich hier nicht anzeigen lässt.
 */
export function FileViewer({
  items,
  index,
  onClose,
  ablegen,
  zielName,
  aktionen,
}: FileViewerProps) {
  const [aktuell, setAktuell] = useState(index);
  /** Solange Editor oder Studio offen sind, deutet der Betrachter keine Tasten. */
  const [werkstattOffen, setWerkstattOffen] = useState(false);
  const [aktionenOffen, setAktionenOffen] = useState(false);
  const datei = items[aktuell];

  // Die Seite dahinter bleibt gesperrt, solange der Betrachter offen ist –
  // auch waehrend Editor oder Studio darueber liegen.
  useEffect(() => {
    const vorher = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = vorher;
    };
  }, []);

  useEffect(() => {
    if (werkstattOffen || aktionenOffen) return undefined;
    const beiTaste = (event: KeyboardEvent) => {
      /*
       * Esc nur, wenn über dem Betrachter WIRKLICH nichts liegt.
       *
       * Der Betrachter meldet sich nicht bei `dialogVerlauf` an – er ist
       * eine Ansicht und soll keinen eigenen Verlaufseintrag anlegen. Damit
       * kennt er den Stapel aber auch nicht, und ein Blatt darüber würde bei
       * einem Esc mit ihm zusammen geschlossen.
       *
       * `dialogeOffen()` allein reichte dafür nicht. Bei einem echten
       * Tastendruck rendert React zwischen zwei Hörern: `dialogVerlauf`
       * schliesst das Blatt, das Blatt meldet sich ab, und wenn dieser Hörer
       * drankommt, ist der Stapel schon leer – das Blatt „Auf den Fernseher"
       * über einem Video nahm den ganzen Betrachter mit. Deshalb markiert
       * `dialogVerlauf` einen Esc, den es verbraucht hat (`defaultPrevented`).
       */
      if (event.defaultPrevented) return;
      if (event.key === 'Escape' && dialogeOffen() === 0) onClose();
      /*
       * Die Pfeile ebenso nur, wenn nichts darüber liegt. Ein Blatt über dem
       * Betrachter (etwa „Auf den Fernseher") oder die Fernsehansicht hören
       * nicht auf sie – blätterte der Betrachter dahinter trotzdem, hängte er
       * mit der Datei das Blatt samt Ansicht aus, mitten im Spiegeln.
       */
      if (dialogeOffen() > 0) return;
      /*
       * Die Pfeile gehören dem Abspieler, wenn er den Fokus hat.
       *
       * Ein `<video controls>` spult mit den Pfeiltasten um fünf Sekunden –
       * und derselbe Druck blätterte hier zur Nachbardatei und räumte das
       * Element gleich mit ab. Mit der Tastatur liess sich im Betrachter
       * also nicht spulen. Dasselbe gilt für Ton und PDF.
       */
      const ziel = event.target;
      if (ziel instanceof HTMLMediaElement || ziel instanceof HTMLObjectElement) return;
      if (event.key === 'ArrowRight') setAktuell((wert) => (wert + 1) % items.length);
      if (event.key === 'ArrowLeft') setAktuell((wert) => (wert - 1 + items.length) % items.length);
    };
    window.addEventListener('keydown', beiTaste);
    return () => window.removeEventListener('keydown', beiTaste);
  }, [items.length, onClose, werkstattOffen, aktionenOffen]);

  if (!datei) return null;

  return createPortal(
    <div className="fv-backdrop" role="dialog" aria-modal="true">
      <header className="fv-bar">
        <button type="button" className="icon-btn" aria-label="Schließen" onClick={onClose}>
          ✕
        </button>
        <div className="fv-title">
          <strong className="truncate">{datei.fileName ?? 'Datei'}</strong>
          <span className="fv-meta">{formatBytes(datei.size)}</span>
        </div>
        {(datei.kind === 'image' || datei.kind === 'video') && (
          <CastKnopf stuecke={[datei.id]} was={datei.fileName ?? 'Diese Datei'} />
        )}
        {datei.kind === 'image' && (
          <FotoWerkstatt
            foto={datei}
            ablegen={ablegen}
            zielName={zielName}
            onOffen={setWerkstattOffen}
          />
        )}
        {datei.kind === 'video' && (
          <VideoWerkstatt
            video={datei}
            ablegen={ablegen}
            zielName={zielName}
            onOffen={setWerkstattOffen}
          />
        )}
        <a
          className="icon-btn"
          href={mediaSrc(datei)}
          target="_blank"
          rel="noreferrer"
          aria-label="In neuem Tab öffnen"
        >
          ↗
        </a>
        {aktionen && (
          <button
            type="button"
            className="icon-btn"
            aria-label="Mehr"
            title="Löschen, teilen, Priorität"
            onClick={() => setAktionenOffen(true)}
          >
            ⋯
          </button>
        )}
      </header>

      <div className="fv-body">
        <Inhalt datei={datei} />
      </div>

      {items.length > 1 && (
        <footer className="fv-bar fv-bar-bottom">
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setAktuell((wert) => (wert - 1 + items.length) % items.length)}
          >
            ‹ Zurück
          </button>
          <span className="fv-meta">
            {aktuell + 1} von {items.length}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => setAktuell((wert) => (wert + 1) % items.length)}
          >
            Weiter ›
          </button>
        </footer>
      )}

      {aktionen && (
        <DateiAktionen
          open={aktionenOffen}
          onClose={() => setAktionenOffen(false)}
          anhaenge={[datei]}
          loeschen={
            aktionen.loeschen
              ? async () => {
                  await aktionen.loeschen!(datei);
                  // Die Datei, die man gerade ansieht, gibt es nicht mehr.
                  // Ohne das bliebe ein Betrachter ohne Inhalt stehen.
                  onClose();
                }
              : undefined
          }
          loeschText={aktionen.loeschText?.(datei)}
          onGeaendert={aktionen.onGeaendert}
        />
      )}
    </div>,
    document.body,
  );
}

/**
 * Video im Vollbildbetrachter – mit echtem Standbild und Fernsehknopf.
 *
 * Eine eigene Komponente, weil beides einen Zustand braucht: `FernsehKnopf`
 * will das ELEMENT, nicht nur seine Adresse, und an ein Element kommt man
 * nur über einen Rückruf-`ref`. In `Inhalt` wäre das ein Haken hinter einem
 * `if`, und das darf React nicht.
 *
 * Kein `poster`: Das wäre die eingebettete Vorschau, und die ist ein Klecks
 * von 160 Punkten Kante – im Vollbild besonders deutlich. Statt dessen
 * springt `standbildHolen` auf ein Zehntel und holt ein echtes Bild aus der
 * Datei. Der Untergrund bleibt hier schwarz und nicht unscharf: In einem
 * Vollbildbetrachter ist Schwarz der richtige Rahmen.
 */
function Video({ datei, quelle }: { datei: AttachmentDto; quelle: string }) {
  const [element, setElement] = useState<HTMLVideoElement | null>(null);
  return (
    <div className="fv-videorahmen">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={setElement}
        className="fv-video"
        src={quelle}
        controls
        playsInline
        preload="metadata"
        onLoadedMetadata={(ereignis) => standbildHolen(ereignis.currentTarget)}
      />
      <FernsehKnopf video={element} anhang={datei} />
    </div>
  );
}

function Inhalt({ datei }: { datei: AttachmentDto }) {
  const quelle = mediaSrc(datei);
  const mime = datei.mime.split(';')[0].trim().toLowerCase();

  if (datei.kind === 'image' || mime.startsWith('image/')) {
    return <img className="fv-image" src={quelle} alt={datei.fileName ?? 'Bild'} />;
  }

  if (datei.kind === 'video' || mime.startsWith('video/')) {
    /*
     * Der Schlüssel legt je Datei ein neues `<video>` an. Ohne ihn behielt
     * „Weiter ›" dasselbe Element und tauschte nur die Adresse – und alles,
     * was am Element hing (die vorbereitete Fernsehkarte, der Zustand des
     * 📺), gehörte dann noch zur vorigen Datei. Nur beim Video: Ein Bild, das
     * sein Element behält, zeigt beim Blättern das alte, bis das neue da ist.
     */
    return <Video key={datei.id} datei={datei} quelle={quelle} />;
  }

  if (datei.kind === 'audio' || mime.startsWith('audio/')) {
    return (
      <div className="fv-card">
        <div className="fv-card-icon" aria-hidden="true">
          🎵
        </div>
        <strong>{datei.fileName ?? 'Tonaufnahme'}</strong>
        {datei.durationMs != null && datei.durationMs > 0 && (
          <span className="fv-meta">{formatDuration(datei.durationMs)}</span>
        )}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio className="fv-audio" src={quelle} controls preload="metadata" />
      </div>
    );
  }

  if (mime === 'application/pdf') {
    // Ein <object> statt <iframe>: Safari auf dem iPhone zeigt PDFs in einem
    // iframe nur als leere Fläche, hier greift wenigstens der Rückfall.
    return (
      <object className="fv-pdf" data={quelle} type="application/pdf" aria-label="PDF">
        <Unbekannt datei={datei} hinweis="Dieses Gerät kann PDFs nicht in der App anzeigen." />
      </object>
    );
  }

  return <Unbekannt datei={datei} />;
}

function Unbekannt({ datei, hinweis }: { datei: AttachmentDto; hinweis?: string }) {
  return (
    <div className="fv-card">
      <div className="fv-card-icon" aria-hidden="true">
        📄
      </div>
      <strong className="truncate">{datei.fileName ?? 'Datei'}</strong>
      <span className="fv-meta">
        {datei.mime} · {formatBytes(datei.size)}
      </span>
      <p className="fv-hint">
        {hinweis ?? 'Dieser Dateityp lässt sich hier nicht anzeigen.'} Zum Öffnen brauchst du eine
        passende App auf dem Gerät.
      </p>
      <a className="btn btn-primary" href={mediaSrc(datei)} target="_blank" rel="noreferrer">
        Öffnen
      </a>
    </div>
  );
}
